import React, { useState, useCallback, useEffect } from 'react';
import { Upload, File, Download, Trash2, ChevronDown, ChevronRight, AlertCircle, X } from 'lucide-react';
import deliveryOperationalService from '../../services/deliveryOperationalService';
import { DeliveryOperationalAttachment } from '../../types/deliveryOperational';
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal';
import toast from 'react-hot-toast';

interface DeliveryOperationalAttachmentListProps {
  operationalItemId: number;
  className?: string;
  forceExpanded?: boolean;
  readOnly?: boolean;
}

export function DeliveryOperationalAttachmentList({
  operationalItemId,
  className = '',
  forceExpanded = false,
  readOnly = false
}: DeliveryOperationalAttachmentListProps) {
  const [attachments, setAttachments] = useState<DeliveryOperationalAttachment[]>([]);
  const [isExpanded, setIsExpanded] = useState(forceExpanded);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    attachment: DeliveryOperationalAttachment | null;
  }>({ isOpen: false, attachment: null });

  const loadAttachments = useCallback(async () => {
    if (!operationalItemId) return;

    try {
      setLoading(true);
      const data = await deliveryOperationalService.getAttachments(operationalItemId);
      setAttachments(data);
    } catch (error) {
      console.error('Error loading attachments:', error);
      toast.error('Erro ao carregar anexos');
    } finally {
      setLoading(false);
    }
  }, [operationalItemId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleFileSelection = (files: FileList) => {
    if (!operationalItemId || readOnly || files.length === 0) return;

    const newFiles = Array.from(files);
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleUploadFiles = async () => {
    if (!operationalItemId || selectedFiles.length === 0) return;

    try {
      setIsUploading(true);
      await deliveryOperationalService.uploadAttachments(operationalItemId, selectedFiles);

      toast.success(`${selectedFiles.length} arquivo(s) enviado(s) com sucesso!`);
      setSelectedFiles([]);
      loadAttachments();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Erro ao enviar arquivos');
    } finally {
      setIsUploading(false);
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (readOnly) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!readOnly) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelection(files);
    }
    e.target.value = '';
  };

  const handleDownload = async (attachment: DeliveryOperationalAttachment) => {
    try {
      const blob = await deliveryOperationalService.downloadAttachment(attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteClick = (attachment: DeliveryOperationalAttachment) => {
    setDeleteModal({ isOpen: true, attachment });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.attachment) return;

    try {
      setDeletingId(deleteModal.attachment.id);
      await deliveryOperationalService.deleteAttachment(deleteModal.attachment.id);
      setAttachments(prev => prev.filter(a => a.id !== deleteModal.attachment!.id));
      toast.success('Arquivo excluído com sucesso!');
      setDeleteModal({ isOpen: false, attachment: null });
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Erro ao excluir arquivo');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, attachment: null });
  };

  const toggleExpanded = () => {
    if (!forceExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  const attachmentCount = attachments.length;
  const hasAttachments = attachmentCount > 0;

  return (
    <div className={className}>
      {/* Header colapsável */}
      <div
        className={`flex items-center justify-between cursor-pointer hover:bg-surface-app rounded-lg p-2 -mx-2 ${forceExpanded ? 'cursor-default' : ''}`}
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-2">
          {!forceExpanded && (
            isExpanded ? <ChevronDown className="w-4 h-4 text-text-tertiary" /> : <ChevronRight className="w-4 h-4 text-text-tertiary" />
          )}
          <File className="w-5 h-5 text-text-tertiary" />
          <span className="text-sm text-text-secondary">Anexos do Item</span>
          <span className="text-xs text-text-tertiary">(clique para gerenciar)</span>
          {hasAttachments && (
            <span className="bg-accent-soft text-info-strong text-xs font-medium px-2 py-0.5 rounded-full">
              {attachmentCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded();
          }}
          className="text-sm text-text-tertiary hover:text-text-secondary"
        >
          {isExpanded ? 'Recolher' : 'Expandir'}
        </button>
      </div>

      {/* Conteúdo expansível */}
      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Upload Area - apenas se não for read-only */}
          {!readOnly && (
            <div
              className={`border-2 border-dashed border-border-strong rounded-lg p-6 text-center transition-colors ${
                isDragOver ? 'border-blue-400 bg-info-soft' : 'hover:border-text-tertiary'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <Upload className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
              <p className="text-sm text-text-secondary mb-2">
                Faça upload de documentos, planilhas, imagens ou outros arquivos relacionados à item
              </p>
              <p className="text-xs text-text-tertiary mb-3">
                Arraste e solte arquivos aqui ou clique para selecionar (máx. 10MB por arquivo)
              </p>
              <input
                type="file"
                multiple
                onChange={handleFileInput}
                className="hidden"
                id={`file-input-operational-${operationalItemId}`}
                disabled={isUploading}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.sql,.json,.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.zip,.rar,.7z"
              />
              <label
                htmlFor={`file-input-operational-${operationalItemId}`}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent hover:bg-accent-hover cursor-pointer ${
                  isUploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isUploading ? 'Enviando...' : 'Selecionar Arquivos'}
              </label>
            </div>
          )}

          {/* Lista de anexos */}
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="text-sm text-text-tertiary mt-2">Carregando anexos...</p>
              </div>
            ) : hasAttachments ? (
              attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between gap-2 p-3 bg-surface-app rounded-lg min-w-0">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <File className="w-5 h-5 text-text-tertiary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate" title={attachment.originalName}>{attachment.originalName}</p>
                      <p className="text-xs text-text-tertiary">
                        {formatFileSize(attachment.fileSize)} • {new Date(attachment.uploadedAt).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleDownload(attachment)}
                      className="p-1 text-accent hover:text-info-strong"
                      title="Baixar arquivo"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(attachment)}
                        disabled={deletingId === attachment.id}
                        className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="Excluir arquivo"
                      >
                        {deletingId === attachment.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
                <p className="text-sm text-text-tertiary">
                  {readOnly
                    ? 'Nenhum anexo encontrado'
                    : 'Nenhum arquivo anexado ainda. Faça upload de seus primeiros arquivos acima.'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Arquivos selecionados para upload */}
          {selectedFiles.length > 0 && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-text-primary">
                  Arquivos selecionados ({selectedFiles.length})
                </h4>
                <button
                  type="button"
                  onClick={clearSelectedFiles}
                  className="text-xs text-text-tertiary hover:text-red-600 font-medium"
                >
                  Limpar todos
                </button>
              </div>

              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex items-center justify-between p-3 bg-surface-app rounded-lg">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <File className="w-4 h-4 text-text-tertiary" />
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(index)}
                      className="ml-3 p-1 hover:bg-red-100 hover:text-red-600 rounded text-text-tertiary hover:text-red-600"
                      title="Remover arquivo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Botão de upload */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={isUploading}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent ${
                    isUploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Enviar {selectedFiles.length} arquivo(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {!readOnly && (
        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Excluir Arquivo"
          itemName={deleteModal.attachment?.originalName}
          isDeleting={deletingId === deleteModal.attachment?.id}
        />
      )}
    </div>
  );
}
